import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../axiosSetup";
import "./PriceList.css";
import Breadcrumbs from "../components/Breadcrumbs";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import CustomTooltip from "../components/CustomTooltip";
import CustomSelect from "../components/CustomSelect";
import { formatMoney } from "../utils/formatMoney";
import { getUser } from "../auth";

const PREVIEW_SIZE = 220;
const sortParamMap = {
  productCode: "orderByProductCode",
  productName: "orderByProductName",
  customerPrice: "orderByCustomerPrice",
};

function ProductImageCell({ item, apiBase, onMouseMove }) {
  const [hasImageError, setHasImageError] = useState(false);

  const imageSrc = item.imageUrl
    ? item.imageUrl.startsWith("https")
      ? item.imageUrl
      : `${apiBase}/customer/product/${item.id}/image`
    : null;

  const showImage = Boolean(imageSrc) && !hasImageError;

  return (
    <div
      className={`pl-image-hover-wrapper${showImage ? "" : " is-empty"}`}
      onMouseEnter={showImage ? (e) => onMouseMove(e, item.id) : undefined}
      onMouseMove={showImage ? (e) => onMouseMove(e, item.id) : undefined}
    >
      {showImage ? (
        <>
          <img
            src={imageSrc}
            style={{
              width:"40",
              height:"40"
            }}
            alt={item.productCode}
            className="pl-product-image"
            onError={() => setHasImageError(true)}
            width={40}
  height={40}

          />

          <div id={`preview-${item.id}`} className="pl-hover-preview">
            <img
              src={imageSrc}
              alt="Preview"
              onError={() => setHasImageError(true)}
            />
          </div>
        </>
      ) : (
        <div className="pl-image-spacer" aria-hidden="true" />
      )}
    </div>
  );
}

export default function PriceListPage() {
  const [priceData, setPriceData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [productTypes, setProductTypes] = useState([]);   // list from backend
  const [productType, setProductType] = useState(""); 
const [searchText, setSearchText] = useState("");
const [currency, setCurrency] = useState("");
const [searchInput, setSearchInput] = useState("");
const [sortField, setSortField] = useState("");
const [sortOrder, setSortOrder] = useState("");
  const typesFetchedRef = React.useRef(false);
  const lastFetchKeyRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
  const loggedInUser = getUser();
const customerName = loggedInUser?.name || "this customer";

useEffect(() => {
  const fetchKey = JSON.stringify({
    page,
    productType,
    searchText,
    sortField,
    sortOrder,
  });

  if (lastFetchKeyRef.current === fetchKey) return;
  lastFetchKeyRef.current = fetchKey;
  const fetchData = async () => {
    try {
      const params = {
        page,
        productType: productType === "All" ? undefined : productType || undefined,
        productCode: searchText || undefined,
      };
      const sortParam = sortParamMap[sortField];
      if (sortParam && sortOrder) {
        params[sortParam] = sortOrder;
      }

      const res = await axios.get(`/customer/price-listing`, {
        params,
      });

      setPriceData(res.data.priceListing);
      setTotalPages(Math.max(1, res.data.totalPages || 0));
      setCurrency(res.data.currency || ""); 
    } catch (err) {
      console.log("Backend not ready, using full mock dataset");
    }
  };

  fetchData();
}, [page, productType, searchText, sortField, sortOrder]);


useEffect(() => {
  if (typesFetchedRef.current) return; 
   typesFetchedRef.current = true;
  const loadProductTypes = async () => {
    try {
      const res = await axios.get("/dropdown/product-type");
      setProductTypes(res.data);
    } catch (err) {
      console.error("Failed to load product types", err);
    }
  };

  loadProductTypes();
}, []);
  const generatePages = () => {
  const pages = [];

  pages.push(1);

  if (page > 3) pages.push("...");

  for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
    pages.push(i);
  }

  if (page < totalPages - 2) pages.push("...");

  // ⛔ only push if different from first page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

const OverflowCell = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span ref={ref} className="pl-truncate">
        {text || "-"}
      </span>
    </CustomTooltip>
  );
};
useEffect(() => {
  const t = setTimeout(() => {
    setPage(1);
    setSearchText(searchInput.trim());
    lastFetchKeyRef.current = null; // force new fetch
  }, 300); // debounce

  return () => clearTimeout(t);
}, [searchInput]);

const handleMouseMove = (event, id) => {
  const preview = document.getElementById(`preview-${id}`);
  if (!preview) return;

  const offset = 18;
  const viewportPadding = 12;

  let left = event.clientX + offset;
  let top = event.clientY + offset;

  if (left + PREVIEW_SIZE > window.innerWidth - viewportPadding) {
    left = event.clientX - PREVIEW_SIZE - offset;
  }

  if (top + PREVIEW_SIZE > window.innerHeight - viewportPadding) {
    top = event.clientY - PREVIEW_SIZE - offset;
  }

  left = Math.max(viewportPadding, left);
  top = Math.max(viewportPadding, top);

  preview.style.left = `${left}px`;
  preview.style.top = `${top}px`;
};

const handleSort = (field) => {
  setPage(1);
  if (sortField !== field) {
    setSortField(field);
    setSortOrder("ASC");
    return;
  }

  if (sortOrder === "ASC") {
    setSortOrder("DESC");
    return;
  }

  setSortField("");
  setSortOrder("");
};

const SortHeader = ({ field, children }) => (
  <button type="button" className="pl-sort-header" onClick={() => handleSort(field)}>
    <span>{children}</span>
    <span className="pl-sort-icons" aria-hidden="true">
      <span className={sortField === field && sortOrder === "ASC" ? "active" : ""}>▲</span>
      <span className={sortField === field && sortOrder === "DESC" ? "active" : ""}>▼</span>
    </span>
  </button>
);

const sortedPriceData = useMemo(() => {
  if (!sortField || !sortOrder) return priceData;

  const getValue = (item) => {
    if (sortField === "productCode") return String(item.productCode || "").toLowerCase();
    if (sortField === "productName") return String(item.productDescription || "").toLowerCase();
    if (sortField === "customerPrice") return Number(item.total) || 0;
    return "";
  };

  return [...priceData].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    const result =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));

    return sortOrder === "ASC" ? result : -result;
  });
}, [priceData, sortField, sortOrder]);

  return (
    <div className="price-container">
      <div className="price-content">
      <Breadcrumbs />
      </div>
      {/* <h2>Price List</h2>
      <p className="section-subtext">View all product prices.</p> */}
    <div className="price-card-wrapper">
      <div className="price-filters">
        <div className="price-header">
  <div>
   
    {/* <h3 className="section-title">Product Pricing</h3>
    <p className="section-subtext">View all product prices.</p> */}
  </div>

  <div className="filters-row">
  <div className="price-filter-item">
    <input
      placeholder="Search product code or description"
      className="search-box"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
    />
  </div>


  <div className="price-filter-item">
    <CustomSelect
      value={productType}
      options={["All", ...productTypes]}
      placeholder="Select Product Type"
      width={"100%"}
      onChange={(v) => {
        setProductType(v);
        setPage(1);
      }}
    />
  </div>

  {/* <button type="button" className="pl-clear-filters-btn" onClick={clearFilters}>
    Clear Filters
  </button> */}

  </div>
</div>

      </div>
      <div className="price-table-wrapper">
      <table className="price-table">
        <thead>
          <tr>
            <th>Product Image</th>
            <th><SortHeader field="productCode">Product Code</SortHeader></th>
            <th><SortHeader field="productName">Product Description</SortHeader></th>
            <th>Product Type</th>
            {/* <th>RRP</th> */}
            {/* <th>Discount Type</th> */}
            {/* <th>Discount</th> */}
            {/* <th>Discount Value</th> */}
            <th><SortHeader field="customerPrice">Customer Unit Price</SortHeader></th>
          </tr>
        </thead>
        <tbody>
           {priceData.length === 0 ? (
    <tr>
      <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
        No products are mapped to {customerName}
      </td>
    </tr>
  ) : (
          sortedPriceData.map((item, idx) => (
            <tr key={idx}>
                {/* ✅ Product Image */}
             <td>
  <ProductImageCell
    item={item}
    apiBase={API_BASE}
    onMouseMove={handleMouseMove}
  />
</td>


              <td>
  <OverflowCell text={item.productCode} />
</td>

<td>
  <OverflowCell text={item.productDescription} />
</td>
              <td>{item.productType}</td>
              {/* <td>
                {item.rrp !== undefined && item.rrp !== null
                  ? `${currency} ${formatMoney(item.rrp)}`
                  : "-"}
              </td> */}
              {/* <td>{item.discountType}</td> */}
              {/* <td>
                {item.discount !== undefined && item.discount !== null
                  ? formatMoney(item.discount)
                  : "-"}
              </td> */}
              {/* <td>{item.discountValue}</td> */}
              <td>
                {item.total !== undefined && item.total !== null
                  ? `${currency} ${formatMoney(item.total)}`
                  : "-"}
              </td>
            </tr>
          ))
        )}
        </tbody>
      </table>
      </div>

      <div className="pl-pagination-frame">
  <div className="pl-pagination-info">
    Page {page} of {totalPages}
  </div>

  <div className="pl-pagination-controls">

    {/* Prev */}
    <button
      className="pl-pagination-arrow"
      disabled={page === 1}
      onClick={() => setPage(page - 1)}
    >
      ‹
    </button>

    {/* Page Numbers */}
    <div className="pl-pagination-numbers">
      {generatePages().map((p, i) =>
        p === "..." ? (
          <span key={i} className="pl-page-ellipsis">...</span>
        ) : (
          <button
            key={i}
            className={`pl-page-number ${page === p ? "active" : ""}`}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        )
      )}
    </div>

    {/* Next */}
    <button
      className="pl-pagination-arrow"
      disabled={page === totalPages}
      onClick={() => setPage(page + 1)}
    >
      ›
    </button>
  </div>
</div>
    </div>
    </div>
  );
}
